import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";

import {
  buildHexWireframeGeometry,
  createHexWireframeMaterial,
  createHexSurfaceMaterial,
  createHaloMaterial,
  HALO_LIFT,
} from "./hex-wireframe";
import { createRingSmoother } from "./smooth-altitudes";
import { AgXOutputShader } from "./agx-output";
import { LensFlareShader } from "./lens-flare";

// The news layer (hex grid, columns, halo) is drawn on this layer, after the
// world has been tone mapped, so its designed colours aren't altered.
const NEWS_LAYER = 1;
// Following (beginFollow): the polar axis the inertial frame turns about, and
// how long after a drag the camera starts easing back to its framing, and
// how quickly.
const FOLLOW_AXIS = new THREE.Vector3(0, 1, 0);
const FOLLOW_REFRAME_DELAY_MS = 1500;
const FOLLOW_REFRAME_MS = 900;

const GLOBE_RADIUS = 100;
// Raw story altitude (base grid level) at which a story's net glow reaches
// full strength and reach.
const NET_WEIGHT_FULL = 0.05;

// Time constant (ms) of the exponential ease between datasets: altitudes and
// hex grow/shrink cover ~95% of the change in 3× this.
const DATA_TRANSITION_MS = 110;
// Hexes leave faster than they arrive so, while scrubbing, few days' worth
// of shrinking hexes overlap (each one costs vertex updates every frame).
const DATA_EXIT_MS = 45;
const ALT_EPSILON = 1e-5;
const PRESENCE_EPSILON = 0.03;
const SURFACE_SWEEP_MS = 200;



// Matches three-globe's lat/lng → cartesian convention: (lat=0, lng=0) → +Z.
// The earth mesh itself is rotated -π/2 around Y so equirectangular textures
// (where the middle pixel is lat=0, lng=0) line up with that same +Z point.
function polar2Cartesian(lat, lng, altitude, radius) {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((90 - lng) * Math.PI) / 180;
  const r = radius * (1 + altitude);
  return new THREE.Vector3(
    r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

export class Globe {
  constructor(container, { width, height, backgroundImageUrl } = {}) {
    this._container = container;
    this._globeRadius = GLOBE_RADIUS;

    const w = width ?? container.clientWidth;
    const h = height ?? container.clientHeight;

    this._scene = new THREE.Scene();

    // Artemis II "Hello, World" photo: 22mm on Nikon D5 full-frame (36×24mm sensor)
    // Vertical FOV = 2 × atan(12/22) ≈ 57°
    // Distance: ~9,889 km from surface + 6,371 km radius = 2.55× Earth radii from centre
    this._camera = new THREE.PerspectiveCamera(57, w / h, 0.1, this._globeRadius * 100);
    this._camera.position.set(0, 0, this._globeRadius * 2.55);

    this._renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this._renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, window.innerWidth < 768 ? 1.5 : 2));
    this._renderer.setSize(w, h);
    this._renderer.setClearColor(0x000000, 1); // Opaque black — prevents white HTML background bleeding through at the dark-side limb
    this._renderer.domElement.setAttribute("role", "img");
    this._renderer.domElement.setAttribute("aria-label", "Interactive news globe. Select a news location to read its articles.");
    container.appendChild(this._renderer.domElement);

    // Linear HDR pipeline for the world (Earth, clouds, atmosphere, Sun,
    // Moon, stars): rendered into a half-float target, then tone mapped (AgX)
    // and converted to sRGB once by the output pass.
    const drawingSize = this._renderer.getDrawingBufferSize(new THREE.Vector2());
    // Multisampling only where it shows: at 2x pixel ratio (retina) edges
    // are already fine, and 4x MSAA on the half-float target was about half
    // the world's GPU time.
    const hdrSamples = window.innerWidth < 768 || this._renderer.getPixelRatio() >= 1.75 ? 0 : 4;
    const hdrTarget = new THREE.WebGLRenderTarget(drawingSize.x, drawingSize.y, {
      type: THREE.HalfFloatType,
      samples: hdrSamples,
    });
    this._composer = new EffectComposer(this._renderer, hdrTarget);
    this._composer.addPass(new RenderPass(this._scene, this._camera));
    // Driven per frame by lens-flare.js updateLensFlare (off unless the Sun is in view).
    this._flarePass = new ShaderPass(LensFlareShader);
    this._flarePass.enabled = false;
    this._composer.addPass(this._flarePass);
    this._outputPass = new ShaderPass(AgXOutputShader);
    this._composer.addPass(this._outputPass);

    this._controls = new OrbitControls(this._camera, this._renderer.domElement);
    this._controls.enableDamping = true;
    this._controls.dampingFactor = 0.1;
    this._controls.enablePan = false;
    this._controls.rotateSpeed = 0.3;
    this._controls.zoomSpeed = 0.2;
    this._controls.minDistance = this._globeRadius * 1.1;
    this._controls.maxDistance = this._globeRadius * 12.5;

    this._isDragging = false;
    this._controls.addEventListener('start', () => { this._isDragging = true; });
    this._controls.addEventListener('end', () => { this._isDragging = false; });

    // On macOS trackpads, two-finger scroll fires wheel events (ctrlKey=false) and
    // pinch-to-zoom fires wheel events with ctrlKey=true. Intercept in capture phase
    // so we can redirect scroll→rotate and leave pinch→zoom for OrbitControls.
    // Velocity accumulator — drained each frame in _animate() for smooth damping.
    this._scrollVX = 0;
    this._scrollVY = 0;
    this._renderer.domElement.addEventListener('wheel', (e) => {
      if (e.ctrlKey) return; // pinch — let OrbitControls handle zoom
      e.preventDefault();
      e.stopPropagation();
      if (this._scrollVX === 0 && this._scrollVY === 0) {
        this._controls.dispatchEvent({ type: 'start' });
      }
      this._scrollVX += e.deltaX;
      this._scrollVY += e.deltaY;
    }, { passive: false, capture: true });


    this._sunLight = new THREE.DirectionalLight(0xffffff, 16.0);
    this._scene.add(this._sunLight);


    this._earthMesh = new THREE.Mesh(
      new THREE.SphereGeometry(this._globeRadius * 0.999, 75, 75),
      new THREE.MeshBasicMaterial({ color: 0x000000 })
    );
    this._earthMesh.rotation.y = -Math.PI / 2;
    this._scene.add(this._earthMesh);

    // Depth-only globe for the news pass, so the far side of the grid stays
    // hidden once the world has been drawn in its own pass.
    this._newsOccluder = new THREE.Mesh(
      new THREE.SphereGeometry(this._globeRadius * 0.999, 75, 75),
      new THREE.MeshBasicMaterial({ colorWrite: false })
    );
    this._newsOccluder.renderOrder = -100;
    this._newsOccluder.layers.set(NEWS_LAYER);
    this._scene.add(this._newsOccluder);
    this._extraOccluders = [];

    this._wireframeMesh = null;

    // Data layer state (see setDataCells). Altitude arrays are indexed by the
    // wireframe's cell order; presence is 0..1 per hex in the surface mesh.
    this._cellIdToInt = new Map(); // stable shader ids, never reassigned
    this._nextCellInt = 1;
    this._rawAlts = new Map();
    this._smoothness = 0;
    this._filled = new Set();
    this._presence = new Map();
    this._altCurrent = null;
    this._altTarget = null;
    this._surfaceCells = null;
    this._dataAnimating = false;
    this._lastSurfaceSweep = 0;
    this._lastFrameTime = performance.now();
    this._tween = null;
    this._altTween = null;
    this._onFrame = null;

    this._lastSelectedPos = new THREE.Vector3(1e6, 1e6, 1e6);
    this._lastSelectedCellId = 0;
    this._currentGlobalFade = 1.0;
    // The whole news layer's presence (setNewsFade): 0 hides it, e.g. at the Moon.
    this._newsFade = 1.0;

    this._wavePoolIdx = 0;
    this._animate = this._animate.bind(this);
    this._handleVisibilityChange = () => {
      if (document.hidden) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      } else if (this._rafId === null) {
        this._animate();
      }
    };
    document.addEventListener("visibilitychange", this._handleVisibilityChange);
    this._animate();
  }

  // Register a callback invoked every animation frame.
  // Pass null to remove it.
  setFrameCallback(fn) { this._onFrame = fn; }

  _animate() {
    if (document.hidden) {
      this._rafId = null;
      return;
    }
    this._rafId = requestAnimationFrame(this._animate);
    if (this._tween) this._tween();
    if (this._altTween) this._altTween();

    const now = performance.now();
    const dt = Math.min(50, now - this._lastFrameTime);
    this._lastFrameTime = now;
    if (this._dataAnimating) this._stepData(dt);

    // Drain two-finger scroll velocity with exponential decay for smooth rotation.
    const wasScrolling = this._scrollVX !== 0 || this._scrollVY !== 0;
    if (wasScrolling) {
      const rotateSpeed = 0.0004;
      const offset = this._camera.position.clone().sub(this._controls.target);
      const spherical = new THREE.Spherical().setFromVector3(offset);
      spherical.theta += this._scrollVX * rotateSpeed;
      spherical.phi   += this._scrollVY * rotateSpeed;
      spherical.phi = Math.max(0.01, Math.min(Math.PI - 0.01, spherical.phi));
      offset.setFromSpherical(spherical);
      this._camera.position.copy(this._controls.target).add(offset);
      this._camera.lookAt(this._controls.target);
      this._controls.dispatchEvent({ type: 'change' });
      this._scrollVX *= 0.9;
      this._scrollVY *= 0.9;
      if (Math.abs(this._scrollVX) < 0.01) this._scrollVX = 0;
      if (Math.abs(this._scrollVY) < 0.01) this._scrollVY = 0;
      if (this._scrollVX === 0 && this._scrollVY === 0) {
        this._controls.dispatchEvent({ type: 'end' });
      }
    }

    if (this._follow) this._stepFollow(dt);
    this._controls.update();

    if (this._wireframeMesh && this._wireframeMesh.material.uniforms.uTime) {
      this._wireframeMesh.material.uniforms.uTime.value = performance.now() / 1000.0;
    }

    // Tick surface material time for button-press animation.
    if (this._surfaceMesh && this._surfaceMesh.material.uniforms.uTime) {
      this._surfaceMesh.material.uniforms.uTime.value = performance.now() / 1000.0;
    }

    if (this._cloudsMesh && this._cloudsMesh.material.userData.uTime) {
      this._cloudsMesh.material.userData.uTime.value = performance.now() / 1000.0;
    }

    if (this._haloMesh && this._haloMesh.material.uniforms.uTime) {
      const halo = this._haloMesh.material.uniforms;
      halo.uTime.value = performance.now() / 1000.0;
      halo.uViewportH.value = this._renderer.getDrawingBufferSize(this._sizeScratch || (this._sizeScratch = new THREE.Vector2())).y;
      halo.uPixelRatio.value = this._renderer.getPixelRatio();
    }

    // Global fade logic: dim unselected hexes when something is selected,
    // but fade them back in immediately when dragging so the grid is clear.
    let targetFade = 1.0;
    if (this._lastSelectedCellId > 0 && !this._isDragging) {
      targetFade = 0.2;
    }
    const lerpSpeed = this._isDragging ? 0.2 : 0.08;
    this._currentGlobalFade += (targetFade - this._currentGlobalFade) * lerpSpeed;

    if (this._surfaceMesh && this._surfaceMesh.material.uniforms.uGlobalFade) {
      this._surfaceMesh.material.uniforms.uGlobalFade.value = this._currentGlobalFade;
    }
    if (this._wireframeMesh && this._wireframeMesh.material.uniforms.uGlobalFade) {
      this._wireframeMesh.material.uniforms.uGlobalFade.value = this._currentGlobalFade;
    }
    if (this._haloMesh && this._haloMesh.material.uniforms.uGlobalFade) {
      this._haloMesh.material.uniforms.uGlobalFade.value = this._currentGlobalFade;
    }
    if (this._cloudsMesh && this._cloudsMesh.material.userData.uGlobalFade) {
      this._cloudsMesh.material.userData.uGlobalFade.value = this._currentGlobalFade;
    }

    if (this._onFrame) this._onFrame();
    this._render();
  }

  _render() {
    this._camera.layers.set(0);
    this._composer.render();
    if (this._newsFade <= 0) {
      this._camera.layers.enableAll();
      return;
    }
    this._renderer.autoClear = false;
    this._renderer.clearDepth();
    this._camera.layers.set(NEWS_LAYER);
    this._renderer.render(this._scene, this._camera);
    this._renderer.autoClear = true;
    this._camera.layers.enableAll();
  }

  // Fades the whole news layer (0..1) through each story's presence, so it
  // shrinks away like stories leaving the window; at 0 it isn't drawn.
  get newsFade() { return this._newsFade; }
  setNewsFade(f) {
    const fade = Math.min(1, Math.max(0, f));
    if (fade === this._newsFade) return;
    this._newsFade = fade;
    this._applyData();
  }

  // Hides the news layer behind another opaque body (the Moon): a depth-only
  // copy of its geometry, a hair smaller, drawn in the news pass.
  addNewsOccluder(mesh) {
    const occluder = new THREE.Mesh(mesh.geometry, this._newsOccluder.material);
    occluder.scale.setScalar(0.999);
    occluder.renderOrder = -100;
    occluder.layers.set(NEWS_LAYER);
    occluder.visible = this._newsOccluder.visible;
    mesh.add(occluder);
    this._extraOccluders.push(occluder);
  }

  // Exposure of the tone-mapped world (eye adaptation).
  setExposure(value) { this._outputPass.uniforms.toneMappingExposure.value = value; }
  get exposure() { return this._outputPass.uniforms.toneMappingExposure.value; }
  get flarePass() { return this._flarePass; }

  // Triggers a wave effect starting at worldPos.
  // Cycles through a pool of MAX_WAVES (4) so multiple waves can coexist.
  triggerWave(worldPos) {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;
    if (this._wireframeMesh && this._wireframeMesh.material.uniforms.uWaveStartPos) {
      const idx = this._wavePoolIdx;
      this._wireframeMesh.material.uniforms.uWaveStartPos.value[idx].copy(worldPos);
      this._wireframeMesh.material.uniforms.uWaveTime.value[idx] = performance.now() / 1000.0;
      
      this._wavePoolIdx = (this._wavePoolIdx + 1) % 4;
    }
  }

  setSize(width, height) {
    this._camera.aspect = width / height;
    this._camera.updateProjectionMatrix();
    this._renderer.setSize(width, height);
    this._composer.setPixelRatio(this._renderer.getPixelRatio());
    this._composer.setSize(width, height);
  }

  // three-globe-shaped accessors so GlobeComponent can keep its existing shape.
  scene() { return this._scene; }
  camera() { return this._camera; }
  renderer() { return this._renderer; }
  controls() { return this._controls; }
  getGlobeRadius() { return this._globeRadius; }

  get earthMesh() { return this._earthMesh; }
  get wireframeMesh() { return this._wireframeMesh; }
  get sunLight() { return this._sunLight; }

  // Where a story's halo (and selection ring) sits: its height on the net
  // plus the lift, as an altitude for getCoords; null if it isn't shown.
  haloAltitude(id) {
    const alt = this._wireframeStoryAltitude?.(id);
    if (alt == null) return null;
    const scale = (this._wireframeOpts && this._wireframeOpts.cellScale) || 1;
    return (this._wireframeRadius / this._globeRadius) * (1 + alt + HALO_LIFT * scale) - 1;
  }

  getCoords(lat, lng, altitude = 0) {
    return polar2Cartesian(lat, lng, altitude, this._globeRadius);
  }

  toGeoCoords({ x, y, z }) {
    const r = Math.hypot(x, y, z);
    const lat = 90 - (Math.acos(y / r) * 180) / Math.PI;
    const rawLng = 90 - (Math.atan2(z, x) * 180) / Math.PI;
    const lng = ((rawLng + 540) % 360) - 180;
    return { lat, lng, altitude: r / this._globeRadius - 1 };
  }

  setCloudsMesh(mesh) {
    this._cloudsMesh = mesh;
  }

  setHexWireframe({
    resolution,
    altitude = 0.005,
    color,
    opacity,
    rimBoost,
    lineWidth,
    pointSize = 4.0,
    useHeatmap = false,
    useElectric = false,
    // A region's cells (zoomed in) instead of every point at `resolution`,
    // and their size relative to the base grid level (highlight and clearance).
    cells = null,
    cellScale = 1.0,
  } = {}) {
    this._wireframeOpts = { color, opacity, rimBoost, lineWidth, pointSize, useHeatmap, useElectric, cellScale };
    // A different grid invalidates the data layer (its cells are other ids).
    const gridKey = cells ? `region:${resolution}:${cells.length}:${cells[0]}:${cells[cells.length - 1]}` : `global:${resolution}`;
    if (gridKey !== this._gridKey) {
      this._gridKey = gridKey;
      this._rawAlts = new Map();
      this._filled = new Set();
      this._presence = new Map();
      this._surfaceCells = null;
      this._smoother = null;
    }
    if (this._wireframeMesh) {
      this._scene.remove(this._wireframeMesh);
      // Global grids are cached (~0.4 s to build); regional ones are not.
      if (!this._wireframeCached) this._wireframeMesh.geometry.dispose();
      this._wireframeMesh.material.dispose();
      this._wireframeMesh = null;
      this._wireframeApply = null;
      this._wireframeSurfaceBuilder = null;
    }
    this._removeSurfaceMeshes();
    this._globalGrids = this._globalGrids || new Map();
    const cacheKey = `${resolution}:${altitude}`;
    let built = cells ? null : this._globalGrids.get(cacheKey);
    if (!built) {
      built = buildHexWireframeGeometry({
        resolution,
        cells,
        radius: this._globeRadius * (1 + altitude),
      });
      if (!cells) this._globalGrids.set(cacheKey, built);
    }
    this._wireframeCached = !cells;
    const { dotGeometry, applyAltitudes, setSurfaceCells, setHiddenCells, numCells } = built;
    const gridCells = built.cells;
    const material = createHexWireframeMaterial({ 
      color, 
      opacity, 
      rimBoost, 
      lineWidth,
      pointSize,
      useHeatmap,
      useElectric,
      isPoints: true,
      globeRadius: this._globeRadius,
      highlightRadius: this._globeRadius * 0.012 * cellScale,
      cellScale,
      netRadius: this._globeRadius * (1 + altitude),
    });

    const mesh = new THREE.Points(dotGeometry, material);

    mesh.renderOrder = 2;
    mesh.layers.set(NEWS_LAYER);
    this._wireframeMesh = mesh;
    this._wireframeApply = applyAltitudes;
    this._wireframeSurfaceBuilder = setSurfaceCells;
    this._wireframeSetHidden = setHiddenCells;
    this._wireframeStoryAltitude = built.storyAltitude;
    this._wireframeRadius = this._globeRadius * (1 + altitude);
    this._scene.add(mesh);

    // Point order is deterministic per level, so altitude arrays and the
    // smoother's cached kernels survive rebuilds (e.g. colour changes).
    if (!this._smoother || this._numCells !== numCells) {
      this._smoother = built.smoother || (built.smoother = createRingSmoother(gridCells));
      this._numCells = numCells;
      this._altCurrent = new Float32Array(numCells);
      this._altTarget = new Float32Array(numCells);
      if (this._rawAlts.size) this._altTarget = this._smoother(this._rawAlts, this._smoothness);
    }
    this._syncSurface();
    this._applyData();
    return mesh;
  }

  // Show a new dataset. rawAltByCellId maps each filled cell to its raw
  // altitude; the wireframe shell uses a smoothed version. Changes
  // ease in over a few hundred ms: altitudes glide, new hexes grow from their
  // centre and removed ones shrink away, so scrubbing reads as continuous.
  // freshness: optional Map<cellId, 0..1>, how recent each cell's news is; it
  // scales the cell's disc and glow like presence does, so stories fade out
  // smoothly as they age rather than disappearing at the window's edge.
  // newness: optional Map<cellId, 0..1>, how new each cell's latest story is;
  // new stories pulse rings of light across the net.
  setDataCells(rawAltByCellId, { smoothness = 0, animate = true, freshness = null, newness = null } = {}) {
    this._rawAlts = rawAltByCellId;
    this._freshness = freshness;
    this._newness = newness;
    this._smoothness = smoothness;
    this._filled = new Set(rawAltByCellId.keys());
    if (!this._smoother) return; // applied once the wireframe exists
    this._altTarget = this._smoother(rawAltByCellId, smoothness);

    const instant = !animate || window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    for (const id of this._filled) {
      if (!this._presence.has(id)) this._presence.set(id, instant ? 1 : 0);
    }
    if (instant) {
      for (const id of [...this._presence.keys()]) {
        if (!this._filled.has(id)) this._presence.delete(id);
      }
      this._altCurrent.set(this._altTarget);
    }
    this._syncSurface();
    this._dataAnimating = !instant;
    this._applyData();
  }

  _stepData(dt) {
    const k = 1 - Math.exp(-dt / DATA_TRANSITION_MS);
    const kExit = 1 - Math.exp(-dt / DATA_EXIT_MS);
    let moving = false;

    const cur = this._altCurrent;
    const tgt = this._altTarget;
    for (let i = 0; i < cur.length; i++) {
      const d = tgt[i] - cur[i];
      if (d === 0) continue;
      if (Math.abs(d) < ALT_EPSILON) cur[i] = tgt[i];
      else { cur[i] += d * k; moving = true; }
    }

    for (const [id, p] of this._presence) {
      const target = this._filled.has(id) ? 1 : 0;
      if (p === target) continue;
      const next = p + (target - p) * (target ? k : kExit);
      if (Math.abs(target - next) < PRESENCE_EPSILON) this._presence.set(id, target);
      else { this._presence.set(id, next); moving = true; }
    }

    // Drop fully-shrunk hexes from the surface mesh. While scrubbing the
    // animation never fully settles, so also sweep periodically — otherwise
    // every hex passed over would pile up in the mesh.
    const now = performance.now();
    if (!moving || now - this._lastSurfaceSweep > SURFACE_SWEEP_MS) {
      this._lastSurfaceSweep = now;
      let removed = false;
      for (const [id, p] of this._presence) {
        if (p === 0 && !this._filled.has(id)) { this._presence.delete(id); removed = true; }
      }
      if (removed) this._syncSurface();
    }
    if (!moving) this._dataAnimating = false;
    this._applyData();
  }

  _applyData() {
    if (!this._wireframeApply || !this._altCurrent) return;
    // Each cell shows at its presence (the grow/shrink animation) times its
    // freshness and the news layer's fade.
    const fresh = this._freshness;
    const fade = this._newsFade;
    const shown = fresh || fade < 1
      ? new Map([...this._presence].map(([id, p]) => [id, p * (fresh?.get(id) ?? 1) * fade]))
      : this._presence;
    this._wireframeApply(this._altCurrent, shown, this._netWeights(), this._newness);
  }

  // How strongly (and how far) each story lights up the net around it
  // (0.1..1), from its raw altitude (article count × importance): square-root
  // scaled, so small stories still show but big ones clearly dominate.
  _netWeights() {
    if (this._netWeightsFor === this._rawAlts) return this._netWeightCache;
    const scale = (this._wireframeOpts && this._wireframeOpts.cellScale) || 1;
    const weights = new Map();
    for (const [id, raw] of this._rawAlts) {
      const w = Math.sqrt(Math.max(0, raw / scale) / NET_WEIGHT_FULL);
      weights.set(id, Math.min(1, Math.max(0.1, w)));
    }
    this._netWeightsFor = this._rawAlts;
    this._netWeightCache = weights;
    return weights;
  }

  _removeSurfaceMeshes() {
    for (const key of ["_surfaceMesh", "_haloMesh", "_beamMesh"]) {
      if (!this[key]) continue;
      this._scene.remove(this[key]);
      this[key].geometry.dispose();
      this[key].material.dispose();
      this[key] = null;
    }
    this._surfaceCells = null;
  }

  // Rebuild the filled-hex surface + halo geometry when the set of hexes in
  // it (filled, or still shrinking out) changes. Materials are kept so
  // selection/press uniforms carry over.
  _syncSurface() {
    if (!this._wireframeSurfaceBuilder) return;
    const ids = [...this._presence.keys()];
    const prev = this._surfaceCells;
    if (prev && prev.length === ids.length && ids.every((id) => prev.has(id))) return;
    this._surfaceCells = new Set(ids);

    for (const id of ids) {
      if (!this._cellIdToInt.has(id)) this._cellIdToInt.set(id, this._nextCellInt++);
    }
    // Stories show as the net's own dots, so none are hidden.
    if (this._wireframeSetHidden) this._wireframeSetHidden(new Set());

    const { surfaceGeometry, haloGeometry, slotTexture, slotTexWidth, surfaceRadius, storyTexture, pulseTexture, storyCount } =
      this._wireframeSurfaceBuilder(ids, this._cellIdToInt);
    // The net's dots fade in around the stories (their centres and presence),
    // and new stories pulse across them.
    if (this._wireframeMesh) {
      const u = this._wireframeMesh.material.uniforms;
      u.uStoryData.value = storyTexture || null;
      u.uStoryPulse.value = pulseTexture || null;
      u.uStoryTexWidth.value = slotTexWidth || 1;
      u.uStoryCount.value = surfaceGeometry ? storyCount : 0;
    }
    const bindSlots = (mesh) => {
      const u = mesh.material.uniforms;
      u.uSlotData.value = slotTexture;
      u.uSlotTexWidth.value = slotTexWidth;
      u.uSurfaceRadius.value = surfaceRadius;
      // Positions are placed in the vertex shader, so the geometry's bounds
      // (unit sphere) don't reflect where it's drawn.
      mesh.frustumCulled = false;
    };

    if (!surfaceGeometry) {
      for (const key of ["_surfaceMesh", "_haloMesh"]) {
        if (!this[key]) continue;
        this._scene.remove(this[key]);
        this[key].material.dispose();
        this[key] = null;
      }
      return;
    }

    if (this._surfaceMesh) {
      this._surfaceMesh.geometry = surfaceGeometry;
    } else {
      const material = createHexSurfaceMaterial({
        color: this._wireframeOpts.color,
        useHeatmap: this._wireframeOpts.useHeatmap,
        useElectric: this._wireframeOpts.useElectric,
        opacity: 1.0,
        rimBoost: this._wireframeOpts.rimBoost,
        globeRadius: this._globeRadius,
        cellScale: this._wireframeOpts.cellScale,
      });
      this._surfaceMesh = new THREE.Mesh(surfaceGeometry, material);
      this._surfaceMesh.layers.set(NEWS_LAYER);
      this._surfaceMesh.renderOrder = 1;
      // No disc: the story is the net's dot at its centre.
      this._surfaceMesh.visible = false;
      this._scene.add(this._surfaceMesh);
    }
    bindSlots(this._surfaceMesh);

    if (this._haloMesh) {
      this._haloMesh.geometry = haloGeometry;
    } else if (haloGeometry) {
      const haloMaterial = createHaloMaterial({
        color: this._wireframeOpts.color,
        opacity: 1.0,
        globeRadius: this._globeRadius,
        cellScale: this._wireframeOpts.cellScale,
      });
      this._haloMesh = new THREE.Points(haloGeometry, haloMaterial);
      this._haloMesh.layers.set(NEWS_LAYER);
      // Render after the prism so the additive halo blooms over its
      // silhouette rather than being clipped by it.
      this._haloMesh.renderOrder = 1.5;
      this._scene.add(this._haloMesh);
    }
    if (this._haloMesh) bindSlots(this._haloMesh);
  }

  setHexHighlight(worldPos, cellId, triggerPress = false) {
    const intId = (cellId && this._cellIdToInt) ? (this._cellIdToInt.get(cellId) || 0) : 0;

    // Always update hover-ring position and hovered-cell ID so the cursor glow
    // and subtle push-in track whichever hex is under the camera/pointer.
    if (this._wireframeMesh && this._wireframeMesh.material.uniforms.uHighlightPos) {
      const wu = this._wireframeMesh.material.uniforms.uHighlightPos.value;
      if (worldPos) wu.copy(worldPos);
      else wu.set(1e6, 1e6, 1e6);
    }
    if (this._surfaceMesh && this._surfaceMesh.material.uniforms.uHoveredCellId !== undefined) {
      this._surfaceMesh.material.uniforms.uHoveredCellId.value = intId;
    }
    if (this._haloMesh && this._haloMesh.material.uniforms.uHoveredCellId !== undefined) {
      this._haloMesh.material.uniforms.uHoveredCellId.value = intId;
    }

    // Only commit the selected-cell state (push-down geometry, press flash,
    // cloud clearance, dot clearance) when the user explicitly snaps/taps.
    if (triggerPress) {
      const isNew = intId !== this._lastSelectedCellId;
      const now = performance.now() / 1000.0;

      if (isNew) {
        const prevId = this._lastSelectedCellId || 0;
        const prevPos = (this._lastSelectedPos || new THREE.Vector3(1e6, 1e6, 1e6)).clone();

        if (this._wireframeMesh) {
          if (this._wireframeMesh.material.uniforms.uUnpressTime) {
            this._wireframeMesh.material.uniforms.uUnpressTime.value = now;
          }
          if (this._wireframeMesh.material.uniforms.uPrevSelectedPos) {
            this._wireframeMesh.material.uniforms.uPrevSelectedPos.value.copy(prevPos);
          }
          if (this._wireframeMesh.material.uniforms.uPressTime) {
            this._wireframeMesh.material.uniforms.uPressTime.value = now;
          }
          if (this._wireframeMesh.material.uniforms.uSelectedPos) {
            const sp = this._wireframeMesh.material.uniforms.uSelectedPos.value;
            if (worldPos) sp.copy(worldPos);
            else sp.set(1e6, 1e6, 1e6);
          }
          if (this._wireframeMesh.material.uniforms.uSelectedCellId) {
            this._wireframeMesh.material.uniforms.uSelectedCellId.value = intId;
          }
        }
        if (this._surfaceMesh) {
          const mat = this._surfaceMesh.material;
          if (mat.uniforms.uUnpressTime) mat.uniforms.uUnpressTime.value = now;
          if (mat.uniforms.uPrevSelectedCellId) mat.uniforms.uPrevSelectedCellId.value = prevId;
          if (mat.uniforms.uSelectedCellId) mat.uniforms.uSelectedCellId.value = intId;
          if (mat.uniforms.uPressedCellId) mat.uniforms.uPressedCellId.value = intId;
          if (mat.uniforms.uPressTime) mat.uniforms.uPressTime.value = now;
          if (mat.uniforms.uSelectedPos) {
            if (worldPos) mat.uniforms.uSelectedPos.value.copy(worldPos);
            else mat.uniforms.uSelectedPos.value.set(1e6, 1e6, 1e6);
          }
          if (mat.uniforms.uPrevSelectedPos) {
            mat.uniforms.uPrevSelectedPos.value.copy(prevPos);
          }
        }
        if (this._haloMesh) {
          const mat = this._haloMesh.material;
          if (mat.uniforms.uUnpressTime) mat.uniforms.uUnpressTime.value = now;
          if (mat.uniforms.uPrevSelectedCellId) mat.uniforms.uPrevSelectedCellId.value = prevId;
          if (mat.uniforms.uSelectedCellId) mat.uniforms.uSelectedCellId.value = intId;
          if (mat.uniforms.uSelectedPos) {
            if (worldPos) mat.uniforms.uSelectedPos.value.copy(worldPos);
            else mat.uniforms.uSelectedPos.value.set(1e6, 1e6, 1e6);
          }
          if (mat.uniforms.uPrevSelectedPos) {
            mat.uniforms.uPrevSelectedPos.value.copy(prevPos);
          }
        }
        if (this._cloudsMesh) {
          const mat = this._cloudsMesh.material;
          if (mat.userData.hexpos) {
            const u = mat.userData.hexpos.value;
            if (worldPos) u.copy(worldPos);
            else u.set(1e6, 1e6, 1e6);
          }
          if (mat.userData.uPressTime) mat.userData.uPressTime.value = now;
          if (mat.userData.uUnpressTime) mat.userData.uUnpressTime.value = now;
          if (mat.userData.uGlobalFade) mat.userData.uGlobalFade.value = this._currentGlobalFade;
          if (mat.userData.uPrevSelectedPos) {
            mat.userData.uPrevSelectedPos.value.copy(prevPos);
          }
        }
        if (this._beamMesh && this._beamMesh.material.uniforms.uSelectedCellId) {
          this._beamMesh.material.uniforms.uSelectedCellId.value = intId;
        }
        if (this._haloMesh) {
          if (this._haloMesh.material.uniforms.uUnpressTime) {
            this._haloMesh.material.uniforms.uUnpressTime.value = now;
          }
          if (this._haloMesh.material.uniforms.uPrevSelectedCellId) {
            this._haloMesh.material.uniforms.uPrevSelectedCellId.value = prevId;
          }
          if (this._haloMesh.material.uniforms.uSelectedCellId) {
            this._haloMesh.material.uniforms.uSelectedCellId.value = intId;
          }
          if (this._haloMesh.material.uniforms.uPressTime) {
            this._haloMesh.material.uniforms.uPressTime.value = now;
          }
        }
        this._lastSelectedCellId = intId;
        this._lastSelectedPos = worldPos ? worldPos.clone() : new THREE.Vector3(1e6, 1e6, 1e6);
      }
    }
  }

  // setHexWireframe (a new grid level) builds fresh materials, dropping the
  // selection; this puts it back as it was, pressed, without replaying the
  // press.
  restoreSelection(worldPos, cellId) {
    this.setHexHighlight(worldPos, cellId);
    const intId = (cellId && this._cellIdToInt.get(cellId)) || 0;
    const past = performance.now() / 1000.0 - 10;
    for (const mesh of [this._wireframeMesh, this._surfaceMesh, this._haloMesh, this._beamMesh]) {
      const u = mesh?.material.uniforms;
      if (!u) continue;
      if (u.uSelectedCellId) u.uSelectedCellId.value = intId;
      if (u.uPressedCellId) u.uPressedCellId.value = intId;
      if (u.uPressTime) u.uPressTime.value = past;
      if (u.uSelectedPos) u.uSelectedPos.value.copy(worldPos);
    }
    this._lastSelectedCellId = intId;
    this._lastSelectedPos = worldPos.clone();
  }

  // Routes hover/cursor updates to the prism highlight while there's no
  // dedicated wireframe cursor visual yet.
  setHudCursor(worldPos, cellId, triggerPress = false) {
    this.setHexHighlight(worldPos, cellId, triggerPress);
  }

  pointOfView({ lat, lng, altitude }, durationMs = 0) {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) durationMs = 0;
    const currentAlt = this._camera.position.length() / this._globeRadius - 1;
    const targetAlt = altitude !== undefined ? altitude : currentAlt;
    const target = this.getCoords(lat, lng, targetAlt);

    if (durationMs <= 0) {
      this._camera.position.copy(target);
      this._controls.update();
      return;
    }

    const start = this._camera.position.clone();
    const startTime = performance.now();
    this._tween = () => {
      const t = Math.min(1, (performance.now() - startTime) / durationMs);
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      this._camera.position.lerpVectors(start, target, e);
      if (t >= 1) this._tween = null;
    };
  }

  /**
   * Smoothly fly the camera to orbit around an arbitrary object.
   * Animates both camera position and controls.target over durationMs.
   *
   * @param {THREE.Vector3} objectPos   — world position of the object to orbit
   * @param {number}        objectRadius — radius of the object (for orbit distance)
   * @param {number}        durationMs  — animation duration
   */
  // Eases the camera's altitude only, keeping where it looks: it runs after
  // any pointOfView tween and alongside dragging, so neither fights it.
  zoomToAltitude(altitude, durationMs = 700) {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) durationMs = 0;
    const from = this._camera.position.length();
    const to = this._globeRadius * (1 + altitude);
    const startTime = performance.now();
    this._altTween = () => {
      const t = durationMs > 0 ? Math.min(1, (performance.now() - startTime) / durationMs) : 1;
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      this._camera.position.setLength(from + (to - from) * e);
      if (t >= 1) this._altTween = null;
    };
  }

  flyToObject(objectPos, objectRadius, durationMs = 1500, onUpdate = null) {
    this._altTween = null;
    if (!this._savedOrbit) {
      this._savedOrbit = {
        minDistance: this._controls.minDistance,
        maxDistance: this._controls.maxDistance,
        target: this._controls.target.clone(),
      };
    }

    const camStart    = this._camera.position.clone();
    const targetStart = this._controls.target.clone();

    // Preserve apparent angular size: D_new / R_new = D_current / R_current
    const currentDist   = camStart.distanceTo(targetStart);
    const distanceRatio = currentDist / this._globeRadius;
    const newDist       = distanceRatio * objectRadius;

    const viewDir = camStart.clone().sub(targetStart).normalize();
    const camEnd  = objectPos.clone().add(viewDir.multiplyScalar(newDist));

    // Widen constraints during the flight so OrbitControls never clamps mid-tween.
    this._controls.minDistance = 0;
    this._controls.maxDistance = Infinity;

    this._orbitTarget = objectPos;
    const startTime = performance.now();

    this._tween = () => {
      const t = Math.min(1, (performance.now() - startTime) / durationMs);
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

      this._camera.position.lerpVectors(camStart, camEnd, e);
      this._controls.target.lerpVectors(targetStart, objectPos, e);

      if (onUpdate) onUpdate(e);

      if (t >= 1) {
        // Apply final constraints only after the camera has arrived.
        this._controls.minDistance = objectRadius * 1.5;
        this._controls.maxDistance = objectRadius * 20;
        this._tween = null;
      }
    };
  }

  /**
   * Return to orbiting Earth at the origin.
   */
  flyBackToEarth(objectRadius, durationMs = 1500, onUpdate = null) {
    this._altTween = null;
    const camStart    = this._camera.position.clone();
    const targetStart = this._controls.target.clone();
    const earthTarget = new THREE.Vector3(0, 0, 0);

    // Preserve apparent angular size: D_earth / R_earth = D_moon / R_moon
    const currentDist   = camStart.distanceTo(targetStart);
    const distanceRatio = currentDist / objectRadius;
    const newDist       = distanceRatio * this._globeRadius;

    const viewDir = camStart.clone().sub(targetStart).normalize();
    const camEnd  = viewDir.multiplyScalar(newDist);

    // Widen constraints during the flight.
    this._controls.minDistance = 0;
    this._controls.maxDistance = Infinity;

    this._orbitTarget = null;
    const startTime = performance.now();

    this._tween = () => {
      const t = Math.min(1, (performance.now() - startTime) / durationMs);
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

      this._camera.position.lerpVectors(camStart, camEnd, e);
      this._controls.target.lerpVectors(targetStart, earthTarget, e);

      if (onUpdate) onUpdate(e);

      if (t >= 1) {
        // Restore Earth orbit constraints only after arrival.
        if (this._savedOrbit) {
          this._controls.minDistance = this._savedOrbit.minDistance;
          this._controls.maxDistance = this._savedOrbit.maxDistance;
        }
        this._tween = null;
      }
    };
  }

  // Following a moving object (the Artemis II replay). `source()` runs each
  // frame before the camera moves and returns { target, frameAngle, framing }.
  // The camera keeps its offset from the target in an inertial frame (turned
  // into the Earth-fixed scene by `frameAngle` about the polar axis), so the
  // view holds still while the Earth turns. Dragging changes the offset; once
  // let go, its length eases back to the `framing` distance. It starts with a
  // flight from the current view to `offset` (Earth-fixed) from the target.
  beginFollow(source, offset, durationMs = 1500) {
    this._tween = null;
    this._altTween = null;
    if (!this._savedOrbit) {
      this._savedOrbit = {
        minDistance: this._controls.minDistance,
        maxDistance: this._controls.maxDistance,
        target: this._controls.target.clone(),
      };
    }
    this._followReturnDistance = this._camera.position.length();
    this._controls.minDistance = 0.5;
    this._controls.maxDistance = this._globeRadius * 60;
    const { target, frameAngle, framing } = source();
    this._follow = {
      source,
      target: target.clone(),
      angle: frameAngle,
      framing,
      offsetInertial: offset.clone().applyAxisAngle(FOLLOW_AXIS, -frameAngle),
      idleSince: performance.now(),
      intro: {
        start: performance.now(),
        duration: durationMs,
        camFrom: this._camera.position.clone(),
        targetFrom: this._controls.target.clone(),
      },
    };
  }

  _stepFollow(dt) {
    const f = this._follow;
    const next = f.source();
    f.target.copy(next.target);
    f.angle = next.frameAngle;
    f.framing = next.framing;
    const now = performance.now();
    const offset = this._camera.position.clone().sub(this._controls.target);
    if (this._isDragging) {
      f.offsetInertial.copy(offset).applyAxisAngle(FOLLOW_AXIS, -f.angle);
      f.idleSince = now;
    } else {
      offset.copy(f.offsetInertial).applyAxisAngle(FOLLOW_AXIS, f.angle);
      if (now - f.idleSince > FOLLOW_REFRAME_DELAY_MS) {
        const len = offset.length();
        offset.setLength(len + (f.framing - len) * (1 - Math.exp(-dt / FOLLOW_REFRAME_MS)));
        f.offsetInertial.setLength(offset.length());
      }
    }
    const camera = f.target.clone().add(offset);
    if (f.intro) {
      const t = Math.min(1, (now - f.intro.start) / f.intro.duration);
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      this._controls.target.lerpVectors(f.intro.targetFrom, f.target, e);
      this._camera.position.lerpVectors(f.intro.camFrom, camera, e);
      if (t >= 1) f.intro = null;
      f.idleSince = now;
    } else {
      this._controls.target.copy(f.target);
      this._camera.position.copy(camera);
    }
  }

  /** Stops following and flies back to orbiting Earth. */
  endFollow(durationMs = 1500) {
    if (!this._follow) return;
    this._follow = null;
    const camStart = this._camera.position.clone();
    const targetStart = this._controls.target.clone();
    const camEnd = camStart.clone().normalize().multiplyScalar(this._followReturnDistance || this._globeRadius * 2.5);
    const earthTarget = new THREE.Vector3();
    this._controls.minDistance = 0;
    this._controls.maxDistance = Infinity;
    const startTime = performance.now();
    this._tween = () => {
      const t = Math.min(1, (performance.now() - startTime) / durationMs);
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      this._camera.position.lerpVectors(camStart, camEnd, e);
      this._controls.target.lerpVectors(targetStart, earthTarget, e);
      if (t >= 1) {
        if (this._savedOrbit) {
          this._controls.minDistance = this._savedOrbit.minDistance;
          this._controls.maxDistance = this._savedOrbit.maxDistance;
          this._savedOrbit = null;
        }
        this._tween = null;
      }
    };
  }

  dispose() {
    cancelAnimationFrame(this._rafId);
    document.removeEventListener("visibilitychange", this._handleVisibilityChange);
    this._controls.dispose();
    this._composer.dispose();
    this._renderer.dispose();
    if (this._container.contains(this._renderer.domElement)) {
      this._container.removeChild(this._renderer.domElement);
    }
  }
}
